import i18n, { type BackendModule, type ResourceKey } from 'i18next';
import { initReactI18next } from 'react-i18next';
import { logger } from '../shared/utils/logger';
import { isValidLocale } from '../shared/constants/locales';
import enTranslations from '../locales/face-verification/en.json';
import enLegalIdTranslations from '../locales/legal-id/en.json';
import enSsnCardTranslations from '../locales/ssnCard/en.json';
import enWorkflowPhoneTranslations from '../locales/workflow-phone/en.json';
import enWorkflowSsnTranslations from '../locales/workflow-ssn/en.json';
import enAddressHistoryTranslations from '../locales/address-history/en.json';
import enConsentTranslations from '../locales/consent/en.json';
import enConfirmInfoTranslations from '../locales/confirmInfo/en.json';
import enCollectAddressTranslations from '../locales/workflow-collect-address/en.json';

// Only `en` (the fallback) is bundled; other locales load on demand (VEX-8108).
// Merged in this order so a later file still wins on a key clash, as with the old static spread.
const LOCALE_DIRS = ['face-verification', 'legal-id', 'consent', 'confirmInfo', 'workflow-phone', 'workflow-ssn', 'ssnCard'];

const localeFiles = import.meta.glob<{ default: Record<string, unknown> }>(
  '../locales/{face-verification,legal-id,consent,confirmInfo,workflow-phone,workflow-ssn,ssnCard}/*.json',
);

export async function loadLocale(lng: string): Promise<Record<string, unknown>> {
  const modules = await Promise.all(LOCALE_DIRS.map((dir) => localeFiles[`../locales/${dir}/${lng}.json`]?.()));
  return Object.assign({}, ...modules.map((module) => module?.default ?? {}));
}

const lazyLocales: BackendModule = {
  type: 'backend',
  init() {},
  read(lng, _ns, callback) {
    loadLocale(lng).then(
      (resources) => callback(null, resources as ResourceKey),
      (error) => callback(error, null),
    );
  },
};

// Start the URL locale's load now, in parallel with React booting.
const urlLocale = typeof window !== 'undefined' ? window.location.pathname.split('/')[1] : '';

i18n.use(lazyLocales).use(initReactI18next).init({
  resources: {
    en: {
      translation: { ...enTranslations, ...enLegalIdTranslations, ...enWorkflowPhoneTranslations, ...enWorkflowSsnTranslations, ...enAddressHistoryTranslations, ...enConsentTranslations, ...enConfirmInfoTranslations, ...enCollectAddressTranslations, ...enSsnCardTranslations },
    },
  },
  partialBundledLanguages: true,
  lng: urlLocale && isValidLocale(urlLocale) ? urlLocale : 'en',
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false,
  },
  react: {
    useSuspense: false, // Disable Suspense to avoid race conditions
  },
  debug: import.meta.env.DEV,
});

// Log language changes
i18n.on('languageChanged', (lng) => {
  logger.log('[i18n] Language changed to:', lng);
});

// Log when i18n is initialized
i18n.on('initialized', (options) => {
  logger.log('[i18n] Initialized with language:', options.lng);
});

export default i18n;
